import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Prospects from './pages/Prospects';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Proposition from './pages/Proposition';
import FicheProspect from './pages/FicheProspect';
import Admin from './pages/Admin';
import ClientsWeb from './pages/ClientsWeb';
import DossierFinancement from './pages/DossierFinancement';
import Dossiers from './pages/Dossiers';
import Agenda from './pages/Agenda';
import Kanban from './pages/Kanban';
import MonProfil from './pages/MonProfil';
import { useWindowSize } from './hooks/useWindowSize';
import Chat from './components/Chat';
import Sidebar, { LARGEUR, LARGEUR_REPLIEE } from './components/Sidebar';
import { API, clearToken, getToken } from './api';
import { C, T } from './theme';

/**
 * Coquille de l'application : menu latéral + zone de contenu.
 *
 * La barre horizontale a laissé place à une colonne (voir components/Sidebar).
 * Le comptage des alertes bancaires vit ici, car il alimente la pastille du
 * menu et doit survivre aux changements de page.
 */
function Coquille({ user, onLogout, children }) {
  const { isMobile } = useWindowSize();
  const [alertCount, setAlertCount] = useState(0);
  const [menuOuvert, setMenuOuvert] = useState(false);

  // L'état replié vit ici, et non dans le menu : la zone de contenu doit
  // décaler sa marge en même temps que la colonne rétrécit. Il est conservé
  // d'une visite à l'autre — c'est une préférence, pas un réglage à refaire.
  const [replie, setReplie] = useState(
    () => localStorage.getItem('menu-replie') === '1'
  );
  useEffect(() => { localStorage.setItem('menu-replie', replie ? '1' : '0'); }, [replie]);

  useEffect(() => {
    axios.get(`${API}/alertes-banques`)
      .then(res => setAlertCount(res.data.length))
      .catch(() => {});
  }, []);

  const decalage = isMobile ? 0 : (replie ? LARGEUR_REPLIEE : LARGEUR);

  return (
    <div style={{ fontFamily: T.police, background: C.fond, minHeight: '100vh' }}>
      <Sidebar
        user={user}
        onLogout={onLogout}
        alertCount={alertCount}
        isMobile={isMobile}
        ouvert={menuOuvert}
        setOuvert={setMenuOuvert}
        replie={replie}
        setReplie={setReplie}
      />
      <main style={{
        marginLeft: decalage,
        paddingTop: isMobile ? 56 : 0,
        transition: 'margin-left 160ms ease',
        minHeight: '100vh',
      }}>
        <div style={{ padding: isMobile ? '16px' : '24px 28px', maxWidth: 1600 }}>
          {children}
        </div>
      </main>
    </div>
  );
}

function EcranChargement() {
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh',
      background: 'linear-gradient(135deg, #1a2b4a 0%, #2d4a7a 100%)',
      fontFamily: "'Segoe UI', Arial, sans-serif", color: 'rgba(255,255,255,0.85)', fontSize: '15px',
    }}>
      Chargement…
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [sessionExpiree, setSessionExpiree] = useState(false);
  const [verificationSession, setVerificationSession] = useState(true);

  // Émis par l'intercepteur axios quand l'API renvoie 401 : on ramène
  // l'utilisateur à l'écran de connexion au lieu de le laisser devant des
  // écrans vides.
  useEffect(() => {
    const surExpiration = () => { setUser(null); setSessionExpiree(true); };
    window.addEventListener('session-expiree', surExpiration);
    return () => window.removeEventListener('session-expiree', surExpiration);
  }, []);

  // Reprise de session au chargement : l'état « user » ne vivait qu'en
  // mémoire, un simple rafraîchissement renvoyait à l'écran de connexion
  // alors que le jeton était toujours valide. On le vérifie auprès du
  // serveur plutôt que de faire confiance au contenu du navigateur.
  useEffect(() => {
    if (!getToken()) { setVerificationSession(false); return; }
    axios.get(`${API}/utilisateurs/me`)
      .then(r => setUser({ id: r.data.id, nom: r.data.nom, role: r.data.role }))
      .catch(() => clearToken())
      .finally(() => setVerificationSession(false));
  }, []);

  if (verificationSession) {
    return <EcranChargement />;
  }

  if (!user) {
    return <Login onLogin={(u) => { setSessionExpiree(false); setUser(u); }} sessionExpiree={sessionExpiree} />;
  }

  const handleLogout = () => { setUser(null); clearToken(); };

  return (
    <Router>
      <Coquille user={user} onLogout={handleLogout}>
        <Chat />
        <Routes>
          <Route path="/" element={<Dashboard user={user} />} />
          <Route path="/prospects" element={<Prospects />} />
          <Route path="/prospects/:id" element={<FicheProspect user={user} />} />
          <Route path="/kanban" element={<Kanban />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/proposition" element={<Proposition />} />
          <Route path="/dossier" element={<DossierFinancement user={user} />} />
          <Route path="/dossiers" element={<Dossiers user={user} />} />
          {/* Réservé aux admins. Le serveur refuse déjà ces appels, mais on
              évite d'afficher un écran d'administration vide et trompeur. */}
          {user.role === 'admin' && <Route path="/admin" element={<Admin />} />}
          {user.role === 'admin' && <Route path="/clients-web" element={<ClientsWeb />} />}
          <Route path="/profil" element={<MonProfil />} />
        </Routes>
      </Coquille>
    </Router>
  );
}

export default App;
